import React, { useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Smartphone, ArrowRight, Check } from "lucide-react";

/**
 * Phone/SMS OTP login modal. If the backend has Twilio configured, a real SMS
 * is sent. Otherwise the backend returns a dev_code we display so the user can
 * still test the flow end-to-end.
 */
export default function PhoneLogin({ onClose, onSuccess }) {
  const { setUser } = useAuth();
  const [phase, setPhase] = useState("phone"); // phone | code | done
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const requestCode = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await api.requestOtp(phone.trim());
      if (res.dev_code) setDevCode(res.dev_code);
      setPhase("code");
    } catch (err) {
      setError(err.data?.detail || "Failed to send code");
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await api.verifyOtp(phone.trim(), code.trim());
      setUser(res.user);
      setPhase("done");
      if (onSuccess) setTimeout(() => onSuccess(res.user), 400);
    } catch (err) {
      setError(err.data?.detail || "Verification failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(11,24,48,0.75)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 60 }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ maxWidth: 420, margin: 0, borderColor: "var(--brass)" }}>
        <div className="hintline" style={{ margin: 0, marginBottom: 8 }}>
          <Smartphone size={13} style={{ verticalAlign: "middle", marginRight: 6 }} />
          SIGN IN WITH PHONE
        </div>

        {phase === "phone" && (
          <>
            <h2>Enter your phone number</h2>
            <p style={{ opacity: 0.8, fontSize: 14 }}>We'll text you a 6-digit code. Use international format including country code.</p>
            <form onSubmit={requestCode}>
              <div className="field">
                <label>Phone number</label>
                <input type="text" inputMode="tel" placeholder="+14155552671" value={phone} onChange={(e) => setPhone(e.target.value)} autoFocus />
              </div>
              {error && <p style={{ color: "var(--coral)", fontSize: 13 }}>{error}</p>}
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 10 }}>
                <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={busy || !phone.startsWith("+")}>
                  {busy ? "Sending…" : <>Send code <ArrowRight size={14} style={{ verticalAlign: "middle", marginLeft: 4 }} /></>}
                </button>
              </div>
            </form>
          </>
        )}

        {phase === "code" && (
          <>
            <h2>Enter the code</h2>
            <p style={{ opacity: 0.8, fontSize: 14 }}>Sent to <strong style={{ color: "var(--parchment)" }}>{phone}</strong>. It expires in 10 minutes.</p>
            {devCode && (
              <div style={{ background: "rgba(201,162,75,0.08)", border: "1px solid var(--brass)", borderRadius: 10, padding: "10px 12px", margin: "6px 0 12px" }}>
                <div className="hintline" style={{ margin: 0, marginBottom: 4 }}>DEV MODE — Twilio not configured</div>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 22, color: "var(--brass-bright)", letterSpacing: "0.15em" }}>{devCode}</div>
                <div style={{ fontSize: 11.5, opacity: 0.65, marginTop: 4 }}>Add TWILIO_* keys to <code>backend/.env</code> for real SMS.</div>
              </div>
            )}
            <form onSubmit={verifyCode}>
              <div className="field">
                <label>6-digit code</label>
                <input type="text" inputMode="numeric" maxLength={6} placeholder="123456" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} autoFocus />
              </div>
              {error && <p style={{ color: "var(--coral)", fontSize: 13 }}>{error}</p>}
              <div style={{ display: "flex", gap: 10, justifyContent: "space-between", marginTop: 10 }}>
                <button type="button" className="btn-secondary" onClick={() => { setPhase("phone"); setCode(""); setError(null); setDevCode(null); }}>Back</button>
                <button type="submit" className="btn-primary" disabled={busy || code.length < 4}>
                  {busy ? "Verifying…" : "Verify & sign in"}
                </button>
              </div>
            </form>
          </>
        )}

        {phase === "done" && (
          <>
            <h2><Check size={22} style={{ verticalAlign: "middle", marginRight: 8, color: "var(--brass-bright)" }} />Signed in</h2>
            <p style={{ opacity: 0.85 }}>Welcome to Bearing.</p>
          </>
        )}
      </div>
    </div>
  );
}
